import { StateCreator } from 'zustand';
import { RootState } from '../rootStore.ts';
import { Context } from '@/api/spec.ts';
import { AppContext, mapContext } from '../../types/AppContext.ts';
import { Exchange, mapExchange } from '../../types/Exchange.ts';
import { ResourceStatus } from '../../types/resource-loader';
import { DEX_EVENTS } from '@/common/DexGlobalEvents.ts';
import { environment } from '../../environment.ts';
import { castDraft } from 'immer';

export interface AppConfigSlice {
  appVersion: string;
  config?: AppContext;
  configStatus?: ResourceStatus;
  exchange?: Exchange;
  // actions
  setConfig: (config: Context) => void;
  subscribeToConfigChanges: () => () => void;
}

const mapAndValidateConfig = (config: Context) => {
  const appContext = mapContext(config);
  const exchange = mapExchange(config);
  if (appContext && exchange) {
    return { appContext, exchange };
  }
  console.error('Invalid app context or exchange data', config);
  return null;
};

export const createAppConfigSlice: StateCreator<RootState, [['zustand/immer', never]], [], AppConfigSlice> = (set, get) => ({
  appVersion: environment.appVersion || '0.0.0',
  config: undefined,
  configStatus: undefined,
  exchange: undefined,

  setConfig: (config) => {
    const validConfig = mapAndValidateConfig(config);
    if (!validConfig) return;

    set((state) => {
      state.config = castDraft(validConfig.appContext);
      state.exchange = validConfig.exchange;
      state.configStatus = 'success';
    });
    get().initializeMarkets(config.markets, validConfig.exchange);
  },

  subscribeToConfigChanges: (): (() => void) => {
    if (typeof ResourceLoader === 'undefined') {
      console.error('ResourceLoader is not available — config preloading script may have been blocked');
      return () => {};
    }

    const configResource = ResourceLoader.getResource('config');

    // Sync the resource's current status into the store — the load may have already
    // finished (success or error) before this subscription ran, in which case no
    // further AppResourceUpdate events will fire.
    if (configResource?.status) {
      set((state) => {
        state.configStatus = configResource.status;
      });
    }

    if (configResource?.status === 'success' && configResource.handle) {
      const configData = configResource.handle as Context;
      const validConfig = mapAndValidateConfig(configData);

      if (validConfig) {
        set((state) => {
          state.config = castDraft(validConfig.appContext);
          state.exchange = validConfig.exchange;
          state.configStatus = 'success';
        });
        get().initializeMarkets(configData.markets, validConfig.exchange);
      }
    }

    const handleResourceUpdate = (event: CustomEvent<{ resourceKey: string; status: ResourceStatus; handle: any }>) => {
      const { detail } = event;

      if (detail.resourceKey !== 'config') return;

      set((state) => {
        state.configStatus = detail.status;

        if (detail.status === 'success' && detail.handle) {
          const configData = detail.handle as Context;
          const validConfig = mapAndValidateConfig(configData);

          if (validConfig) {
            state.config = castDraft(validConfig.appContext);
            state.exchange = validConfig.exchange;
          }
        }
      });

      if (detail.handle && get().exchange) {
        const configData = detail.handle as Context;
        const validConfig = mapAndValidateConfig(configData);
        if (validConfig) {
          get().initializeMarkets(configData.markets, validConfig.exchange);
        }
      }
    };

    // Attach event listeners
    window.addEventListener(DEX_EVENTS.APP_RESOURCE_UPDATE_EVENT, handleResourceUpdate as EventListener);

    return () => {
      window.removeEventListener(DEX_EVENTS.APP_RESOURCE_UPDATE_EVENT, handleResourceUpdate as EventListener);
    };
  },
});
